/**
 * HighlightsSection - Pure Presentation Component
 * 
 * ✓ STATELESS - Receives state from parent (highlightIndex, setHighlightIndex)
 * ✓ REUSABLE - Works in Homepage, CMS, Approval Preview
 * ✓ PURE - Only receives data props and renders
 * 
 * Props:
 *   data: {
 *     kicker: string,
 *     title: string,
 *     intro: string,
 *     liveLabel: string,
 *     liveNote: string,
 *     items: array
 *   }
 *   highlightIndex: number (current carousel position)
 *   onHighlightChange: function (called when user clicks dot or item)
 *   images: array (photo array for display)
 *   editableMode: boolean (optional)
 *   selectedSection: string (optional)
 *   onEditSection: function (optional)
 *   onOpenItemManager: function (optional)
 */
function HighlightsSection({
  data,
  highlightIndex = 0,
  onHighlightChange,
  images = [],
  editableMode = false,
  selectedSection = null,
  onEditSection = null,
  onOpenItemManager = null,
}) {
  // ALWAYS render highlights section with data (no empty placeholders)
  
  const {
    kicker = 'Highlights',
    title = 'School Highlights',
    intro = 'Explore our achievements',
    liveLabel = '',
    items = [
      { title: 'Academic Excellence', summary: 'Excellence in learning', media_id: 1 },
      { title: 'Student Life', summary: 'Vibrant campus', media_id: 2 }
    ]
  } = data || {};

  const activeHighlight = items[highlightIndex] || items[0];  // Guard: fallback to first item
  
  // CRITICAL GUARD: Ensure activeHighlight has required properties
  if (!activeHighlight || !activeHighlight.title) {
    console.warn('[HighlightsSection] WARNING: activeHighlight is missing or has no title', activeHighlight);
    // This should never happen now, but gracefully handle it
    return (
      <section className="highlights-section" aria-label="Highlights">
        <div className="highlights-shell">
          <div className="highlights-header">
            <p className="highlights-kicker">{kicker}</p>
            <h2 className="highlights-title">{title}</h2>
            <p className="highlights-intro">{intro}</p>
          </div>
          <div style={{padding: '40px', textAlign: 'center', color: '#999'}}>
            Loading highlights...
          </div>
        </div>
      </section>
    );
  }
  
  // Get image from images array by index
  // Get image from images array by index
  let activeImage = null;
  if (images && images.length > highlightIndex && images[highlightIndex]) {
    activeImage = images[highlightIndex];
  }
  // Check activeHighlight.image or photo
  if (!activeImage && activeHighlight) {
    activeImage = activeHighlight.image || activeHighlight.photo || null;
  }
  // Fallback if images array too short
  if (!activeImage && images && images.length > 0) {
    activeImage = images[highlightIndex % images.length];
  }

  const handleDotClick = (index) => {
    if (onHighlightChange) {
      onHighlightChange(index);
    }
  };

  const handleItemClick = (index) => {
    if (onHighlightChange) {
      onHighlightChange(index);
    }
  };

  return (
    <section className="highlights-section" aria-label="Highlights">
      <div className="highlights-shell">
        <div className="highlights-header">
          <p className="highlights-kicker">{kicker}</p>
          <h2 className="highlights-title">{title}</h2>
          <p className="highlights-intro">{intro}</p>
        </div>

        <div className="highlights-grid">
          <article className="highlight-feature-card">
            <div className="highlight-feature-image-wrap">
              {activeImage ? (
                <img
                  src={activeImage}
                  alt={activeHighlight.title}
                  className="highlight-feature-image"
                  onError={(e) => {
                    console.warn('[HighlightsSection] Feature image failed to load:', activeImage);
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="highlight-feature-image" style={{backgroundColor: '#e2e8f0'}} />
              )}
              <div className="highlight-image-overlay"></div>
            </div>
            <div className="highlight-feature-content">
              <div className="highlight-feature-topline">
                <span className="highlight-live-pill">
                  <span className="live-dot"></span>
                  {liveLabel || "Live update"}
                </span>
              </div>
              <h3>{activeHighlight.title}</h3>
              <p>{activeHighlight.summary}</p>
              <div className="highlight-progress" aria-hidden="true">
                {items.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`highlight-progress-dot ${index === highlightIndex ? 'active' : ''}`}
                    onClick={() => handleDotClick(index)}
                    aria-label={`View highlight ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </article>

          <div className="highlight-news-list-wrapper">
            <div className="highlight-news-list" aria-label="More highlights">
              {items.map((item, index) => (
                <article 
                  key={item.title} 
                  className={`highlight-news-item ${index === highlightIndex ? 'active' : ''}`}
                  onClick={() => handleItemClick(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleItemClick(index);
                    }
                  }}
                >
                  <div className="highlight-news-item-header">
                    <span className="highlight-news-index">{index < 9 ? `0${index + 1}` : index + 1}</span>
                    <svg className="highlight-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </div>
                  <div className="highlight-news-thumb-wrap">
                    {(() => {
                      const thumbImg = (images && images[index]) || item.image || item.photo || (images && images.length > 0 ? images[index % images.length] : null);
                      return thumbImg ? (
                        <img
                          src={thumbImg}
                          alt={item.title}
                          className="highlight-news-thumb"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="highlight-news-thumb-fallback">
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                          </svg>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="highlight-news-content">
                    <h3>{item.title}</h3>
                    <p>{item.summary}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>

        {editableMode && selectedSection === 'highlights' && onEditSection && (
          <div className="cms-section-overlay cms-section-overlay-bottom" onClick={(e) => e.stopPropagation()}>
            <div className="cms-section-overlay-badge">Highlights Section</div>
            <div className="cms-section-overlay-buttons">
              <button 
                className="cms-overlay-btn cms-overlay-btn-edit"
                onClick={() => onEditSection('highlights')}
              >
                Edit Header
              </button>
              <button 
                className="cms-overlay-btn cms-overlay-btn-items"
                onClick={() => onOpenItemManager?.('highlights')}
              >
                Manage Items
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default HighlightsSection;
